import { type AdminClientFinanceSummary } from '@carsfinders/contracts';
import { Plus } from 'lucide-react';
import { useId } from 'react';

import { Button } from '../../../../components/ui';
import { t } from '../../../../lib/i18n';
import { formatChf } from './finance-format';
import { cardClass } from './finance-ui';

/**
 * Summary row of the client «Фінанси» tab: Депозит · Борг за рахунками ·
 * Прострочено · Стеля ставок, and the ONE entry for received money,
 * «+ Додати платіж». Every figure is the read model's — nothing is summed here.
 */
export function ClientFinanceSummary({
  summary,
  onAddPayment,
}: {
  summary: AdminClientFinanceSummary;
  onAddPayment: () => void;
}) {
  const headingId = useId();
  const tiles = [
    {
      key: 'deposit',
      label: t('clients.finance.summary.deposit'),
      value: formatChf(summary.depositChf),
      tone: 'text-cf-ink',
    },
    {
      key: 'owed',
      label: t('clients.finance.summary.owed'),
      value: formatChf(summary.invoicesOwedChf),
      tone: summary.invoicesOwedChf > 0 ? 'text-cf-warn' : 'text-cf-ink',
    },
    {
      key: 'overdue',
      label: t('clients.finance.summary.overdue'),
      value: formatChf(summary.overdueChf),
      tone: summary.overdueChf > 0 ? 'text-cf-danger' : 'text-cf-ink',
    },
    {
      key: 'ceiling',
      label: t('clients.finance.summary.ceiling'),
      value: summary.ceilingChf === null ? '—' : formatChf(summary.ceilingChf),
      tone: 'text-cf-ink',
    },
  ];

  return (
    <section aria-labelledby={headingId} data-testid="finance-summary" className={cardClass}>
      <h2 id={headingId} className="sr-only">
        {t('clients.finance.summary.aria')}
      </h2>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <dl className="m-0 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
          {tiles.map((tile) => (
            <div
              key={tile.key}
              data-testid={`finance-summary-${tile.key}`}
              className="flex flex-col gap-1"
            >
              <dt className="text-[12px] text-cf-ink-subtle">{tile.label}</dt>
              <dd className={`m-0 font-mono text-[16px] font-semibold lg:text-[18px] ${tile.tone}`}>
                {tile.value}
              </dd>
            </div>
          ))}
        </dl>
        <Button
          type="button"
          onClick={onAddPayment}
          data-testid="finance-add-payment"
          className="gap-1.5 self-start lg:self-auto"
        >
          <Plus size={16} strokeWidth={2} aria-hidden />
          {t('clients.finance.summary.addPayment')}
        </Button>
      </div>
    </section>
  );
}
